// src/pages/QRCodeGenerator.js
import React, { useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar"; // ✅ just import sidebar once
import SEOHelmet from "../../../components/SEOHelmet";

export default function QRCodeGenerator() {
  const [text, setText] = useState("");
  const qrRef = useRef();
  const navigate = useNavigate();

  const [size, setSize] = useState(150);
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [format, setFormat] = useState("png");


  const downloadQRCode = () => {
    const canvas = qrRef.current.querySelector("canvas");
    let url;

    if (format === "png") {
      url = canvas.toDataURL("image/png");
    } else if (format === "jpg") {
      url = canvas.toDataURL("image/jpeg");
    } else if (format === "svg") {
      // For SVG, we need to manually build XML (optional enhancement)
      alert("SVG export can be implemented here.");
      return;
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-code.${format}`;
    a.click();
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar (handles both mobile + desktop) */}
      <Sidebar />




      {/* Main Content */}
      <div className="flex-1 md:ml-64">
      <SEOHelmet
  title="QR Code Generator"
  description="Create free QR codes instantly with our online QR Code Generator. Customize your QR codes, track scans, and share securely."
  keywords="QR code generator, create QR code, QR code online, free QR code, QR code maker"
  url="https://omkarmore.com/qrgenerator"
  image="https://omkarmore.com/LOGO_512.png"
/>

        {/* Sticky Header */}
        <div className="sticky top-0 bg-gray-50 z-30 py-4 flex items-center gap-4 px-6 shadow-sm">
          <button
            onClick={() => navigate("/tools")}
            className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-600 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Tools
          </button>
        </div>

        {/* Page Content */}
        <main className="bg-white p-6 md:p-8 rounded-2xl shadow-lg 
                     max-w-4xl w-full 
                     mx-4 md:ml-8 mt-24 md:mt-12">
          <header className="text-center md:text-left mb-10">
            <h1 className="text-3xl md:text-4xl font-extrabold text-cyan-700">
              QR Code Generator
            </h1>
            <p className="mt-3 text-gray-600 text-base md:text-lg">
              Create custom QR codes instantly. Enter text or a link, customize the style, and download your QR code for free.
            </p>
          </header>

          <section className="space-y-8">
            {/* Input Box */}
            <div>
              <label
                htmlFor="qr-text"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Enter text or URL
              </label>
              <input
                id="qr-text"
                type="text"
                placeholder="https://example.com"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base shadow-sm 
                   focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
              />
            </div>

            {/* Extra Options */}
            {text && (
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                {/* QR Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    QR Size
                  </label>
                  <input
                    type="range"
                    min="150"
                    max="400"
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Adjust the slider to increase or decrease the QR code size.
                    Current: <span className="font-semibold">{size}px</span>
                  </p>
                </div>

                {/* Foreground Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foreground Color
                  </label>
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-full h-10 cursor-pointer"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Pick the color for the QR code pattern (black by default).
                  </p>
                </div>

                {/* Background Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-full h-10 cursor-pointer"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Choose a background color for your QR code (white by default).
                  </p>
                </div>
              </div>
            )}

            {/* QR Code Display */}
            {text && (
              <div className="flex flex-col items-center gap-2 mt-8 animate-fadeIn">
                <QRCodeCanvas value={text} size={size} fgColor={fgColor} bgColor={bgColor} />
                <p className="text-gray-600 text-sm">Preview of your customized QR Code</p>
              </div>
            )}

            {/* Download Button with Format */}
            {text && (
              <div className="text-center space-y-4 mt-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Download Format
                  </label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="png">PNG (best for web)</option>
                    <option value="jpg">JPG (smaller size)</option>
                    <option value="svg">SVG (infinite scaling)</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Select the file type you want to save your QR code in.
                  </p>
                </div>

                <button
                  onClick={downloadQRCode}
                  className="px-8 py-3 bg-cyan-700 text-white font-semibold rounded-full shadow-md hover:bg-cyan-600 hover:shadow-lg transition"
                >
                  Download QR Code
                </button>
                <p className="text-gray-500 text-sm">
                  Tip: Larger sizes are ideal for printing, while smaller sizes work best online.
                </p>
              </div>
            )}
          </section>
        </main>


      </div>
    </div>
  );
}
